import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/inbox.css';
import { installTriageEngine, triageEmail, triageEmailLive, triageOpenCenter } from '../components/triageEngine';
import { pbAuthedFetch } from '../authHeaders';
import { WorkRequestModal } from '../components/WorkRequestModal';
import { ServiceNowTicketModal, type TicketIncident } from '../components/ServiceNowTicketModal';
import { AiSummaryModal } from '../components/AiSummaryModal';

// ─── TypeScript interfaces ────────────────────────────────────────────────────
interface OutlookMessage {
  id: string;
  from: string;
  fromEmail?: string;
  to?: string[];
  subject: string;
  snippet: string;
  body?: string;
  bodyType?: 'html' | 'text';
  received?: string;
  unread?: boolean;
  importance?: 'high' | 'normal' | 'low';
  flagged?: boolean;
  hasAttachments?: boolean;
}

interface FolderCounts {
  [folder: string]: {
    unread?: number;
    total?: number;
  };
}

interface FolderData {
  ok: boolean;
  folders: FolderCounts;
}

interface MessagesData {
  ok: boolean;
  messages: OutlookMessage[];
  unread?: number;
  total?: number;
  mailbox?: string;
}

interface MessageDetail {
  ok: boolean;
  message: OutlookMessage;
}

interface ModalState {
  title: string;
  body: string;
  footer: string;
}

// ─── Auth helpers ─────────────────────────────────────────────────────────
function _pbJsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json' };
}

// ─── Utility functions ────────────────────────────────────────────────────────
function _esc(s: unknown): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function _initials(name: string | undefined): string {
  if (!name) return '—';
  const p = String(name).trim().split(/\s+/);
  return (((p[0] || '')[0] || '') + ((p[p.length - 1] || '')[0] || '')).toUpperCase() || '—';
}

// Sender display strings in this page often carry a role/org suffix
// ("Susan Williamson · Division Chief") — the avatar only wants the person's
// own name, so this stops at the first separator before computing initials.
const AVATAR_PALETTE = ['#6B4EA6', '#B0499A', '#0B6A5C', '#0C5A92', '#8A5A00', '#A13D5A', '#3B6E22', '#5A4FCF'];
function _avatarName(from: string | undefined): string {
  return String(from || '').split(/[·|(—-]/)[0].trim();
}
function _avatarInitials(from: string | undefined): string {
  return _initials(_avatarName(from));
}
function _avatarColor(from: string | undefined): string {
  const s = _avatarName(from) || 'x';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

const _PB_DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const _PB_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function _pbRelTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if ((now.getTime() - d.getTime()) / 86400000 < 7 && d < now) {
    return _PB_DOW[d.getDay()];
  }
  return _PB_MON[d.getMonth()] + ' ' + d.getDate();
}

// Kept for potential triage center usage
const __CHECK_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span>';
const _BOLT_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 1.5L3 9h4l-1 5.5L13 7H9l1-5.5z" fill="currentColor" stroke="none"/></svg></span>';
const _TRASH_SVG = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4.5 4.5l.6 8.4a1 1 0 0 0 1 .93h3.8a1 1 0 0 0 1-.93l.6-8.4"/></svg></span>';

// ─── Folder definitions ───────────────────────────────────────────────────────
const PB_FOLDERS: Record<string, string> = {
  inbox: 'Inbox',
  drafts: 'Drafts',
  sent: 'Sent',
  archive: 'Archive',
  important: 'Important',
  flagged: 'Awaiting Reply',
};

// ─── Component ────────────────────────────────────────────────────────────────
export function InboxPage() {
  const [helpOpen, setHelpOpen] = useState(false);
  const [focusedTab, setFocusedTab] = useState<'focused' | 'other'>('focused');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [snTicket, setSnTicket] = useState<TicketIncident | null>(null);
  const [aiEmailSummaryOpen, setAiEmailSummaryOpen] = useState(false);
  const [workRequestOpen, setWorkRequestOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const inboxStateRef = useRef<{ folder: string; selected: OutlookMessage | null; connectorOn: boolean }>({
    folder: 'inbox',
    selected: null,
    connectorOn: false,
  });

  // ─── Helper functions ─────────────────────────────────────────────────────
  const getList = useCallback(() => {
    return pageRef.current?.querySelector('.mail-list') || null;
  }, []);

  const pbInboxApi = useCallback((path: string, opts?: RequestInit) => {
    return pbAuthedFetch(path, opts);
  }, []);

  const _pbRenderBody = useCallback((container: HTMLElement | null, mm: OutlookMessage | null, fallbackSnippet: string) => {
    if (!container) return;
    container.innerHTML = '';
    container.classList.toggle('ib-has-frame', !!(mm && mm.bodyType === 'html'));
    if (mm && mm.bodyType === 'html') {
      const frame = document.createElement('iframe');
      frame.setAttribute('sandbox', '');
      frame.setAttribute('title', 'Email message body');
      frame.setAttribute('referrerpolicy', 'no-referrer');
      frame.className = 'ib-body-frame';
      frame.srcdoc = String((mm && mm.body) || '');
      container.appendChild(frame);
    } else {
      const p = document.createElement('p');
      p.textContent = (mm && mm.body) || fallbackSnippet || '(no preview available)';
      container.appendChild(p);
    }
  }, []);

  const _pbBodyText = useCallback((e: Partial<OutlookMessage>) => {
    return String(e.body || e.snippet || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 4000);
  }, []);

  const _pbClearNotice = useCallback((list: Element) => {
    const n = list.querySelector('.pb-folder-notice');
    if (n) n.remove();
  }, []);

  const _pbShowDemo = useCallback((list: Element) => {
    _pbClearNotice(list);
    list.querySelectorAll('.mail-item').forEach((n) => {
      (n as HTMLElement).style.display = '';
    });
  }, [_pbClearNotice]);

  const _pbHideDemo = useCallback((list: Element, msg: string) => {
    _pbClearNotice(list);
    list.querySelectorAll('.mail-item').forEach((n) => {
      (n as HTMLElement).style.display = 'none';
    });
    const el = document.createElement('div');
    el.className = 'mail-item pb-folder-notice';
    el.textContent = msg;
    list.appendChild(el);
  }, [_pbClearNotice]);

  // ─── pbCurrentEmail ───────────────────────────────────────────────────────
  const pbCurrentEmail = useCallback((): Partial<OutlookMessage> => {
    if (inboxStateRef.current.selected) return inboxStateRef.current.selected;
    const d = pageRef.current?.querySelector('.mail-detail');
    if (!d) return { from: '', subject: '', body: '' };
    const txt = (sel: string) => {
      const el = d.querySelector(sel);
      return el ? el.textContent?.trim() || '' : '';
    };
    const bodyEl = d.querySelector('.mail-d-body') as HTMLElement | null;
    return {
      from: txt('.mail-d-from'),
      fromEmail: txt('.mail-d-fromemail'),
      subject: txt('.mail-d-subj'),
      body: bodyEl ? (bodyEl.innerText || bodyEl.textContent || '') : '',
    };
  }, []);

  // ─── pbRenderMessages ─────────────────────────────────────────────────────
  const pbRenderMessages = useCallback((list: Element, data: MessagesData, key: string) => {
    _pbClearNotice(list);
    list.querySelectorAll('.mail-item').forEach((n) => n.remove());
    const page = pageRef.current;
    const msgs = data.messages || [];
    const t = list.querySelector('.mail-list-t');
    if (t) t.textContent = PB_FOLDERS[key];
    const lc = list.querySelector('.mail-list-c');
    if (lc) lc.textContent = (data.unread || 0) + ' unread · ' + (data.total || msgs.length) + ' shown';
    const sub = page?.querySelector('.pagehead .pagehead__eyebrow');
    if (sub) sub.textContent = (data.mailbox || 'Outlook') + ' · Outlook · ' + (data.unread || 0) + ' unread';
    if (!msgs.length) {
      _pbHideDemo(list, 'No messages in ' + PB_FOLDERS[key] + '.');
      return;
    }
    msgs.forEach((m) => {
      const item = document.createElement('div');
      item.className = 'mail-item' + (m.unread ? ' unread' : '');
      item.style.cursor = 'pointer';
      const who = (key === 'sent' || key === 'drafts') && m.to && m.to.length ? 'To: ' + m.to.join(', ') : m.from;
      const tags: string[] = [];
      if (m.importance === 'high') tags.push('<span class="mail-tg imp">Important</span>');
      if (m.flagged) tags.push('<span class="mail-tg act">Flagged</span>');
      if (m.hasAttachments) tags.push('<span class="mail-tg fyi">Attachment</span>');
      const showTriage = key !== 'drafts' && key !== 'sent';
      const triageBtn = showTriage
        ? '<button class="mail-triage-btn">' + _BOLT_SVG + ' Triage</button>'
        : '';
      const deleteBtn = '<button class="mail-delete-btn" type="button" title="Delete" aria-label="Delete email">' + _TRASH_SVG + '</button>';
      const tagsRow = '<div class="mail-tags">' + tags.join('') + triageBtn + deleteBtn + '</div>';
      item.innerHTML =
        '<div class="mail-row1"><span class="mail-av" style="background:' + _avatarColor(who) + '">' + _esc(_avatarInitials(who)) + '</span><div class="mail-from">' + _esc(who) + '</div><div class="mail-time">' + _esc(_pbRelTime(m.received)) + '</div></div>' +
        '<div class="mail-subj">' + _esc(m.subject) + '</div>' +
        '<div class="mail-snippet">' + _esc(m.snippet) + '</div>' +
        tagsRow;
      if (showTriage) {
        const tb = item.querySelector('.mail-triage-btn');
        if (tb) tb.addEventListener('click', (ev) => {
          ev.stopPropagation();
          pbTriageLive(m.id);
        });
      }
      const db = item.querySelector('.mail-delete-btn');
      if (db) db.addEventListener('click', (ev) => {
        ev.stopPropagation();
        pbDeleteMessage(m.id);
      });
      item.addEventListener('click', () => pbInboxSelect(m, item as HTMLElement));
      list.appendChild(item);
    });
    if (msgs[0]) pbInboxSelect(msgs[0], list.querySelector('.mail-item') as HTMLElement | null);
  }, [_pbClearNotice, _pbHideDemo]);

  // ─── pbHydrateFolder ──────────────────────────────────────────────────────
  const pbHydrateFolder = useCallback(async (folder: string) => {
    const key = PB_FOLDERS[folder] ? folder : 'inbox';
    inboxStateRef.current.folder = key;
    const list = getList();
    if (!list) return;
    let data: MessagesData;
    try {
      const res = await pbInboxApi('/api/integrations/outlook/messages?folder=' + encodeURIComponent(key));
      if (res.status === 400) {
        inboxStateRef.current.connectorOn = false;
        if (key === 'inbox') _pbShowDemo(list);
        else _pbHideDemo(list, 'Connect the Outlook connector to view ' + PB_FOLDERS[key] + '.');
        return;
      }
      data = await res.json().catch(() => ({})) as MessagesData;
      if (!res.ok || !data.ok) {
        _pbHideDemo(list, 'Could not load ' + PB_FOLDERS[key] + ' from Outlook.');
        return;
      }
    } catch (_e) {
      _pbHideDemo(list, 'Could not reach Outlook.');
      return;
    }
    inboxStateRef.current.connectorOn = true;
    pbRenderMessages(list, data, key);
  }, [getList, pbInboxApi, _pbShowDemo, _pbHideDemo, pbRenderMessages]);

  // ─── pbSelectFolder ───────────────────────────────────────────────────────
  const pbSelectFolder = useCallback((folder: string, el: HTMLElement | null) => {
    const page = pageRef.current;
    if (!page) return;
    page.querySelectorAll('.mail-fld.active').forEach((n) => n.classList.remove('active'));
    // A folder can appear twice (pinned under Favorites + its real entry under
    // the account tree) — sync both instances' active state, not just whichever
    // one was clicked, so Favorites' pin never silently falls out of step.
    page.querySelectorAll(`.mail-fld[data-folder="${folder}"]`).forEach((n) => n.classList.add('active'));
    void el;
    const search = page.querySelector('.mail-search input') as HTMLInputElement | null;
    if (search) search.value = '';
    if (PB_FOLDERS[folder]) {
      pbHydrateFolder(folder);
      return;
    }
    const list = getList();
    if (list) _pbHideDemo(list, 'This is a saved view — no live Outlook folder is mapped to it.');
  }, [pbHydrateFolder, getList, _pbHideDemo]);

  // ─── pbLoadFolderCounts ───────────────────────────────────────────────────
  const pbLoadFolderCounts = useCallback(async () => {
    try {
      const res = await pbInboxApi('/api/integrations/outlook/folders');
      if (!res.ok) return;
      const data = await res.json().catch(() => ({})) as FolderData;
      if (!data.ok) return;
      const page = pageRef.current;
      if (!page) return;
      Object.keys(data.folders || {}).forEach((k) => {
        const badge = page.querySelector('.mail-fld[data-folder="' + k + '"] .mail-fld-c');
        if (badge) badge.textContent = String(data.folders[k].unread || data.folders[k].total || 0);
      });
    } catch (_e) { /* keep demo counts */ }
  }, [pbInboxApi]);

  // ─── pbRefreshInbox ───────────────────────────────────────────────────────
  const pbRefreshInbox = useCallback(() => {
    pbHydrateFolder(inboxStateRef.current.folder);
    pbLoadFolderCounts();
  }, [pbHydrateFolder, pbLoadFolderCounts]);

  // ─── pbInboxSelect ────────────────────────────────────────────────────────
  const pbInboxSelect = useCallback(async (m: OutlookMessage, itemEl: HTMLElement | null) => {
    inboxStateRef.current.selected = m;
    const page = pageRef.current;
    if (!page) return;
    page.querySelectorAll('.mail-item.active').forEach((n) => n.classList.remove('active'));
    if (itemEl) itemEl.classList.add('active');
    const d = page.querySelector('.mail-detail');
    if (!d) return;
    const set = (sel: string, txt: string) => {
      const el = d.querySelector(sel);
      if (el) el.textContent = txt;
    };
    set('.mail-d-subj', m.subject || '');
    set('.mail-d-from', m.from || '');
    set('.mail-d-fromemail', m.fromEmail || '');
    set('.mail-d-av', _avatarInitials(m.from));
    const avEl = d.querySelector('.mail-d-av') as HTMLElement | null;
    if (avEl) avEl.style.background = _avatarColor(m.from);
    const time = d.querySelector('.mail-d-time');
    if (time) time.innerHTML = '<strong>' + _esc(_pbRelTime(m.received)) + '</strong>';
    const to = d.querySelector('.mail-d-to');
    if (to) to.innerHTML = '<strong>To:</strong> ' + _esc((m.to && m.to.join(', ')) || 'connected mailbox');
    const body = d.querySelector('.mail-d-body') as HTMLElement | null;
    if (body) {
      body.classList.remove('ib-has-frame');
      body.innerHTML = '<div class="ib-loading">' +
        '<span class="ib-skel-line"></span><span class="ib-skel-line"></span><span class="ib-skel-line"></span>' +
        '<div class="ib-loading-cap">Loading message…</div></div>';
    }
    try {
      const res = await pbInboxApi('/api/integrations/outlook/message/' + encodeURIComponent(m.id));
      if (res.ok) {
        const full = await res.json().catch(() => null) as MessageDetail | null;
        if (full && full.ok && full.message) {
          inboxStateRef.current.selected = { ...m, ...full.message };
          _pbRenderBody(body, full.message, m.snippet);
          return;
        }
      }
    } catch (_e) { /* fall through to snippet */ }
    _pbRenderBody(body, null, m.snippet);
  }, [pbInboxApi, _pbRenderBody]);

  // ─── pbTriageLive ─────────────────────────────────────────────────────────
  const pbTriageLive = useCallback(async (id: string) => {
    let email: OutlookMessage | null = inboxStateRef.current.selected && inboxStateRef.current.selected.id === id
      ? inboxStateRef.current.selected
      : null;
    if (!email || !email.body) {
      try {
        const res = await pbInboxApi('/api/integrations/outlook/message/' + encodeURIComponent(id));
        const full = res.ok ? await res.json().catch(() => null) as MessageDetail | null : null;
        if (full && full.ok) email = full.message;
      } catch (_e) { /* use what we have */ }
    }
    if (typeof (window as any).triageEmailLive === 'function') {
      (window as any).triageEmailLive(email || { subject: '', from: '', body: '' });
    }
  }, [pbInboxApi]);

  // ─── Compose functions ────────────────────────────────────────────────────
  const pbComposeModal = useCallback(({ to = '', cc = '', subject = '', body = '', title = 'Compose' }: {
    to?: string;
    cc?: string;
    subject?: string;
    body?: string;
    title?: string;
  } = {}) => {
    const form = `<div class="ib-form">
      <label class="ib-field"><span class="ib-label">To</span><input id="pb-cmp-to" class="ib-input" value="${_esc(to)}" placeholder="name@agency.gov"></label>
      <label class="ib-field"><span class="ib-label">Cc</span><input id="pb-cmp-cc" class="ib-input" value="${_esc(cc)}"></label>
      <label class="ib-field"><span class="ib-label">Subject</span><input id="pb-cmp-subj" class="ib-input" value="${_esc(subject)}"></label>
      <label class="ib-field"><span class="ib-label">Message</span><textarea id="pb-cmp-body" class="ib-textarea" rows="9">${_esc(body)}</textarea></label>
      <div id="pb-cmp-status" class="ib-status"></div>
    </div>`;
    setModal({
      title: _esc(title),
      body: form,
      footer: '<button class="btn btn--sm" data-tc="closeMo" data-tc-args="[]">Cancel</button><button class="btn btn--primary btn--sm" data-tc="pbComposeConfirm" data-tc-args="[]">Send</button>',
    });
  }, []);

  const pbComposeConfirm = useCallback(() => {
    const status = document.getElementById('pb-cmp-status');
    const to = (document.getElementById('pb-cmp-to') as HTMLInputElement | null)?.value || '';
    if (!to.trim()) {
      if (status) {
        status.textContent = 'Add at least one recipient.';
        status.className = 'ib-status is-err';
      }
      return;
    }
    if (status) {
      status.innerHTML = '<strong>This sends a real email from the connected mailbox.</strong> Confirm to proceed.';
      status.className = 'ib-status is-note';
    }
    const f = document.getElementById('mf');
    if (f) f.innerHTML = '<button class="btn btn--sm" data-tc="closeMo" data-tc-args="[]">Cancel</button><button class="btn btn--primary btn--sm" data-tc="pbComposeSend" data-tc-args="[]">Confirm — send now</button>';
  }, []);

  const pbComposeSend = useCallback(async () => {
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value || '';
    const to = val('pb-cmp-to');
    const cc = val('pb-cmp-cc');
    const subject = val('pb-cmp-subj');
    const bodyText = val('pb-cmp-body');
    const status = document.getElementById('pb-cmp-status');
    if (status) {
      status.textContent = 'Sending…';
      status.className = 'ib-status is-busy';
    }
    try {
      const res = await pbInboxApi('/api/integrations/outlook/send', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({ to, cc, subject, body: _esc(bodyText).replace(/\n/g, '<br>'), bodyType: 'HTML' }),
      });
      const data = await res.json().catch(() => ({}));
      const f = document.getElementById('mf');
      if (res.ok && data.ok) {
        if (status) {
          status.innerHTML = '✓ Sent to ' + _esc((data.to || []).join(', '));
          status.className = 'ib-status is-ok';
        }
        if (f) f.innerHTML = '<button class="btn btn--primary btn--sm" data-tc="closeMo" data-tc-args="[]">Done</button>';
        if (inboxStateRef.current.connectorOn) pbLoadFolderCounts();
      } else {
        const msg = res.status === 400 ? (data.error || 'Outlook connector not configured or invalid recipients.') : (data.error || 'Send failed.');
        if (status) {
          status.textContent = '✗ ' + msg;
          status.className = 'ib-status is-err';
        }
        if (f) f.innerHTML = '<button class="btn btn--sm" data-tc="closeMo" data-tc-args="[]">Close</button>';
      }
    } catch (_e) {
      if (status) {
        status.textContent = '✗ Could not reach the server.';
        status.className = 'ib-status is-err';
      }
    }
  }, [pbInboxApi, pbLoadFolderCounts]);

  const pbReplySelected = useCallback(() => {
    const e = pbCurrentEmail();
    const quoted = '\n\n----- Original message -----\nFrom: ' + (e.from || '') + '\nSubject: ' + (e.subject || '') + '\n\n' + _pbBodyText(e);
    pbComposeModal({ to: e.fromEmail || e.from || '', subject: 'RE: ' + (e.subject || ''), body: quoted, title: 'Reply' });
  }, [pbCurrentEmail, _pbBodyText, pbComposeModal]);

  const pbForwardSelected = useCallback(() => {
    const e = pbCurrentEmail();
    const quoted = '\n\n----- Forwarded message -----\nFrom: ' + (e.from || '') + '\nSubject: ' + (e.subject || '') + '\n\n' + _pbBodyText(e);
    pbComposeModal({ to: '', subject: 'FW: ' + (e.subject || ''), body: quoted, title: 'Forward' });
  }, [pbCurrentEmail, _pbBodyText, pbComposeModal]);

  const pbOpenCompose = useCallback(() => {
    pbComposeModal({ title: 'Compose' });
  }, [pbComposeModal]);

  // ─── pbFlagSelected ───────────────────────────────────────────────────────
  const _pbToast = useCallback((msg: string) => {
    let t = document.getElementById('pb-inbox-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'pb-inbox-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.opacity = '1';
    clearTimeout((_pbToast as any)._t);
    (_pbToast as any)._t = setTimeout(() => {
      t.style.opacity = '0';
    }, 2600);
  }, []);

  const pbFlagSelected = useCallback(async () => {
    const e = inboxStateRef.current.selected;
    if (!e || !e.id) {
      _pbToast('Select a live message to flag.');
      return;
    }
    try {
      const res = await pbInboxApi('/api/integrations/outlook/flag', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({ messageId: e.id, flagged: !e.flagged }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        e.flagged = data.flagged;
        _pbToast(data.flagged ? 'Message flagged in Outlook.' : 'Flag cleared.');
      } else {
        _pbToast('Flag not applied (' + (data.error || res.status) + ').');
      }
    } catch (_er) {
      _pbToast('Could not reach the server.');
    }
  }, [pbInboxApi, _pbToast]);

  // ─── pbDeleteMessage ──────────────────────────────────────────────────────
  const pbDeleteMessage = useCallback(async (id: string) => {
    if (!id) {
      _pbToast('Select a live message to delete.');
      return;
    }
    if (!window.confirm('Delete this email? It moves to Deleted Items in Outlook.')) return;
    try {
      const res = await pbInboxApi('/api/integrations/outlook/delete', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({ messageId: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        _pbToast('Message moved to Deleted Items.');
        if (inboxStateRef.current.selected && inboxStateRef.current.selected.id === id) {
          inboxStateRef.current.selected = null;
        }
        pbRefreshInbox();
      } else {
        _pbToast('Delete not applied (' + (data.error || res.status) + ').');
      }
    } catch (_er) {
      _pbToast('Could not reach the server.');
    }
  }, [pbInboxApi, _pbToast, pbRefreshInbox]);

  // ─── pbConvertSelected ────────────────────────────────────────────────────
  // Converts the currently-selected email into a real task, a correspondence
  // log entry, and a real ServiceNow incident.
  const pbConvertSelected = useCallback(async () => {
    const email = inboxStateRef.current.selected;
    if (!email) {
      _pbToast('Select an email first.');
      return;
    }
    try {
      const taskRes = await pbAuthedFetch('/api/tasks', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({
          title: email.subject || 'Untitled task',
          description: email.snippet || '',
          source: 'inbox-triage',
          source_ref: email.id,
        }),
      });
      if (!taskRes.ok) throw new Error(`Request failed: ${taskRes.status}`);
      const { task } = await taskRes.json();

      const corrRes = await pbAuthedFetch('/api/correspondence', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({
          task_id: task.id,
          subject: email.subject || '',
          from_addr: email.fromEmail || email.from || '',
          snippet: email.snippet || '',
          direction: 'inbound',
          source: 'outlook',
          source_message_id: email.id,
        }),
      });
      if (!corrRes.ok) throw new Error(`Request failed: ${corrRes.status}`);

      const incidentRes = await pbAuthedFetch('/api/integrations/servicenow/incident', {
        method: 'POST',
        headers: _pbJsonHeaders(),
        body: JSON.stringify({
          short_description: email.subject || 'Untitled task',
          description: email.snippet || '',
        }),
      });
      if (incidentRes.ok) {
        const incident = await incidentRes.json();
        _pbToast(`Task created — filed as ${incident.number} in ServiceNow.`);
      } else {
        // Task + correspondence still succeeded even if the connector isn't
        // configured yet — say so rather than claiming full failure.
        _pbToast('Task created. ServiceNow incident could not be filed (connector not configured).');
      }
    } catch (_err) {
      _pbToast('Could not convert this email to a task.');
    }
  }, [_pbToast]);

  // ─── pbInboxSearch ────────────────────────────────────────────────────────
  const pbInboxSearch = useCallback((q: string) => {
    const list = getList();
    if (!list) return;
    const term = String(q || '').trim().toLowerCase();
    list.querySelectorAll('.mail-item').forEach((it) => {
      if (it.classList.contains('pb-folder-notice')) return;
      (it as HTMLElement).style.display = (!term || it.textContent?.toLowerCase().includes(term)) ? '' : 'none';
    });
  }, [getList]);

  // ─── Bolt icon component ──────────────────────────────────────────────────
  const Bolt = () => (
    <span className="ico">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path d="M9 1.5L3 9h4l-1 5.5L13 7H9l1-5.5z" fill="currentColor" stroke="none" />
      </svg>
    </span>
  );
  const Spark = () => (
    <span className="ico">
      <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1.5l1.5 4 4 1.5-4 1.5L8 12.5 6.5 8.5 2.5 7l4-1.5z" />
      </svg>
    </span>
  );

  // ─── Event handlers ───────────────────────────────────────────────────────
  const heroEmail: Partial<OutlookMessage> = {
    from: 'Susan Williamson · Division Chief',
    subject: 'Roadblock — RAVEn ADE-3 stakeholder dependency closure delayed',
    body:
      "Steve — quick note before the 1100 meeting. The Office of General Counsel just told me their reviewer for the RAVEn dependency closure is on family leave until 27 May. That's 5 days after our scheduled 22 May ADE-3 review. We have a problem. I need your team's recommendation in writing by COB today on: (a) can the review proceed on 22 May with OGC concurrence in absentia, (b) if we slip the review, what's the minimum slip that preserves the FY26 Q3 funding line, and (c) who from your team can pick up the cross-walk OGC was supposed to do. Also — the RAVEn query latency issue from this morning's stand-up: does it affect anything in the ADE-3 package?",
  };

  const heroTriage = () => triageEmailLive(heroEmail as OutlookMessage);
  const heroTriageBtn = (e: React.MouseEvent) => {
    e.stopPropagation();
    triageEmailLive(heroEmail as OutlookMessage);
  };

  const triageBtn = (key: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const src = ((window as any).TRIAGE_EMAILS || {})[key] || {};
    triageEmailLive({ from: src.from || '', subject: src.subject || '', body: src.bodyHtml || src.subject || '' });
  };

  const folderClick = (folder: string) => (e: React.MouseEvent<HTMLDivElement>) => {
    pbSelectFolder(folder, e.currentTarget);
  };

  const folderKeyDown = (folder: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pbSelectFolder(folder, e.currentTarget as HTMLDivElement);
    }
  };

  const fldProps = (folder: string) => ({
    'data-folder': folder,
    role: 'button' as const,
    tabIndex: 0,
    style: { cursor: 'pointer' },
    onClick: folderClick(folder),
    onKeyDown: folderKeyDown(folder),
  });

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    installTriageEngine();
    (window as any).triageEmail = triageEmail;
    (window as any).pbTriageLive = pbTriageLive;
    (window as any).pbComposeConfirm = pbComposeConfirm;
    (window as any).pbComposeSend = pbComposeSend;
    (window as any).closeMo = () => setModal(null);
    (window as any).openMo = (title: string, body: string, footer: string) => setModal({ title, body, footer });
    (window as any).triageOpenCenter = triageOpenCenter;
    // Bridge for triageEngine.ts's generated recommendation HTML (which runs
    // outside React, via the data-tc click-delegation mechanism) to open a
    // real React-owned ServiceNowTicketModal — same window-function pattern
    // openMo/closeMo already use.
    (window as any).openServiceNowTicket = (payloadJson: string) => {
      try {
        setSnTicket(JSON.parse(payloadJson));
      } catch {
        // malformed payload — no-op rather than throwing into the click handler
      }
    };
    // triageEngine.ts calls window.pbToast(msg, type) for live-Jira-filed /
    // apply-failed feedback — it was never wired to a real toast, so those
    // calls silently did nothing. This page's own toast has no type styling,
    // but showing the message is what matters.
    (window as any).pbToast = (msg: string) => _pbToast(msg);
  }, [pbTriageLive, pbComposeConfirm, pbComposeSend, _pbToast]);

  useEffect(() => {
    pbHydrateFolder('inbox');
    pbLoadFolderCounts();
  }, [pbHydrateFolder, pbLoadFolderCounts]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="page active" id="page-inbox" ref={pageRef}>
      <div className="ib-ribbon">
        <div className="ib-ribbon__tabs">
          <span className="ib-ribbon__tab">File</span>
          <span className="ib-ribbon__tab ib-ribbon__tab--active">Home</span>
          <span className="ib-ribbon__tab">View</span>
          <span className="ib-ribbon__tab">Help</span>
        </div>
        <div className="ib-ribbon__cmds">
          <button className="ib-ribbon__primary" onClick={() => pbOpenCompose()}>
            <span className="ico">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <path d="M9.5 2.5l4 4L5 15l-4 1 1-4z" />
              </svg>
            </span> New mail
          </button>
          <span className="ib-ribbon__sep" aria-hidden="true" />
          <button className="ib-ribbon__icon" title="Delete selected" onClick={() => pbDeleteMessage(inboxStateRef.current.selected?.id || '')}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4.5 4.5l.6 8.4a1 1 0 0 0 1 .93h3.8a1 1 0 0 0 1-.93l.6-8.4" />
            </svg>
          </button>
          <button className="ib-ribbon__icon" title="Refresh" onClick={() => pbRefreshInbox()}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M14 8a6 6 0 1 1-2-4.5" />
              <path d="M14 2v3.5h-3.5" />
            </svg>
          </button>
          <button className="ib-ribbon__icon" title="Flag selected" onClick={() => pbFlagSelected()}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M3 14V2" />
              <path d="M3 2h9l-2 3 2 3H3z" fill="currentColor" />
            </svg>
          </button>
          <span className="ib-ribbon__sep" aria-hidden="true" />
          <button className="ib-ribbon__cmd" onClick={() => triageOpenCenter()}>
            <Bolt /> Triage Center
          </button>
          <button className="ib-ribbon__cmd" onClick={() => setWorkRequestOpen(true)}>
            <span className="ico">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <rect x="2" y="3" width="12" height="10" rx="1.3" />
                <path d="M5 6.5h6M5 9h4" />
              </svg>
            </span> Work request
          </button>
          <button className="ib-ribbon__cmd" title="Walk through what everything on this page does" aria-haspopup="dialog" aria-expanded={helpOpen} onClick={() => setHelpOpen(true)}>
            <span className="ico">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M6.1 6.1a1.9 1.9 0 1 1 2.7 1.7c-.5.3-.8.6-.8 1.2" />
                <circle cx="8" cy="11.4" r=".5" fill="currentColor" />
              </svg>
            </span> Page guide
          </button>
        </div>
      </div>
      <div className="mail-wrap">
        {/* FOLDERS */}
        <div className="mail-folders-box">
          <div className="mail-folders">
            <div className="mail-fld-sec">Favorites</div>
            <div className="mail-fld active" {...fldProps('inbox')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="1" y="2" width="12" height="9" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
                <path d="M1 4l6 4 6-4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Inbox<span className="mail-fld-c">12</span>
            </div>
            <div className="mail-fld" {...fldProps('sent')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 7l5 5 5-9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sent Items
            </div>
            <div className="mail-fld" {...fldProps('drafts')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 12V3a1 1 0 0 1 1-1h6l3 3v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Drafts<span className="mail-fld-c">3</span>
            </div>

            <div className="mail-fld-acct">steve.palmer@itpms.contractor.dhs.gov</div>
            <div className="mail-fld" {...fldProps('inbox')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="1" y="2" width="12" height="9" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
                <path d="M1 4l6 4 6-4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Inbox<span className="mail-fld-c">12</span>
            </div>
            <div className="mail-fld" {...fldProps('drafts')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 12V3a1 1 0 0 1 1-1h6l3 3v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Drafts<span className="mail-fld-c">3</span>
            </div>
            <div className="mail-fld" {...fldProps('sent')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 7l5 5 5-9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sent
            </div>
            <div className="mail-fld" {...fldProps('archive')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 3l4 4 4-4M3 8l4 4 4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Archive
            </div>
            <div className="mail-fld-sec">Flags</div>
            <div className="mail-fld" {...fldProps('important')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 1v12M3 2h7l-1.5 2.5L10 7H3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Important<span className="mail-fld-c">5</span>
            </div>
            <div className="mail-fld" {...fldProps('flagged')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" />
                <path d="M7 4v3l2 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              Awaiting Reply
            </div>
            <div className="mail-fld-sec">DHS</div>
            <div className="mail-fld" {...fldProps('c3lab')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              C3 Lab (Fairfax)
            </div>
            <div className="mail-fld" {...fldProps('nefl')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              NEFL (NYC)
            </div>
            <div className="mail-fld" {...fldProps('contractor')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              Contractor (ITPMS)
            </div>
            <div className="mail-fld" {...fldProps('ocio')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              OCIO Leadership
            </div>
            <div className="mail-fld" {...fldProps('taskers')}>
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              Taskers
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="mail-list-box">
          <div className="mail-list">
            <div className="mail-list-top">
              <div className="ib-focus-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={focusedTab === 'focused'}
                  className={'ib-focus-tab' + (focusedTab === 'focused' ? ' is-active' : '')}
                  onClick={() => setFocusedTab('focused')}
                >
                  Focused
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={focusedTab === 'other'}
                  className={'ib-focus-tab' + (focusedTab === 'other' ? ' is-active' : '')}
                  onClick={() => setFocusedTab('other')}
                >
                  Other
                </button>
              </div>
              <div className="mail-list-h">
                <div>
                  <div className="mail-list-t">Inbox</div>
                  <div className="mail-list-c">12 unread · 184 total</div>
                </div>
                <div className="ib-list-tools">
                  <span className="pill pill-nv">By date ▾</span>
                </div>
              </div>
              <div className="mail-search search">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <circle cx="7" cy="7" r="4.5" />
                  <path d="M10.4 10.4L14 14" />
                </svg>
                <input placeholder="Search inbox…" aria-label="Search inbox…" onChange={(e) => pbInboxSearch(e.target.value)} />
              </div>
            </div>

            {focusedTab === 'other' ? (
              <div className="ib-other-empty">
                <p className="state__title">Nothing in Other</p>
                <p className="state__body">Low-priority mail Stella routes out of Focused will collect here.</p>
              </div>
            ) : (
            <>
            <div className="mail-day">Today</div>
            <div className="mail-item unread mail-item--flagged" onClick={heroTriage} role="button" tabIndex={0}>
              <span className="ib-flag-chip"><Bolt /> Stella flagged</span>
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor(heroEmail.from) }}>{_avatarInitials(heroEmail.from)}</span>
                <div className="mail-from">Susan Williamson · Division Chief</div>
                <div className="mail-time">09:38</div>
              </div>
              <div className="mail-subj">Roadblock — RAVEn ADE-3 stakeholder dependency closure delayed</div>
              <div className="mail-snippet">Steve — quick note before the 1100 meeting. The Office of General Counsel just told me that their reviewer for the RAVEn dependency closure is on family leave until 27 May. That's 5 days after our scheduled 22 May ADE-3 review. We have a problem…</div>
              <div className="mail-tags">
                <span className="mail-tg rbk">
                  <span className="ico">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                      <rect x="2" y="6" width="12" height="4" />
                      <path d="M2 6l3-2 3 2 3-2 3 2M2 10l3 2 3-2 3 2 3-2" />
                    </svg>
                  </span> Roadblock
                </span>
                <span className="mail-tg act">Action</span>
                <button onClick={heroTriageBtn} className="mail-triage-btn"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread active">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('Steve Palmer') }}>{_avatarInitials('Steve Palmer')}</span>
                <div className="mail-from">Steve Palmer (ITPMS)</div>
                <div className="mail-time">07:42</div>
              </div>
              <div className="mail-subj">[DELIVERABLE] Weekly Contractor Status — Wk Ending 02 May 2026</div>
              <div className="mail-snippet">Per §6.4 — all programs, all task areas. RAVEn-FL pilot reaching first test gate this week…</div>
              <div className="mail-tags">
                <span className="mail-tg act">Action</span>
                <span className="mail-tg tsk">Sign-off</span>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('DHS HQ') }}>{_avatarInitials('DHS HQ')}</span>
                <div className="mail-from">DHS HQ — Tasker Mgmt System</div>
                <div className="mail-time">06:15</div>
              </div>
              <div className="mail-subj">DATA CALL: FY26 IT Spend by Program — HSI Components (Due 09 May)</div>
              <div className="mail-snippet">DHS CIO requesting consolidated spend by L2/L3 investment — Steve, your branch is on the distro…</div>
              <div className="mail-tags">
                <span className="mail-tg imp">Important</span>
                <button onClick={triageBtn('datacall')} className="mail-triage-btn" data-triage="datacall"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-day">Last week</div>
            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('D. Vargas') }}>{_avatarInitials('D. Vargas')}</span>
                <div className="mail-from">C3 Lab (Fairfax) — D. Vargas (Lab IT Manager)</div>
                <div className="mail-time">Mon</div>
              </div>
              <div className="mail-subj">Cellebrite license expansion — request for 8 add'l seats for Op STELLAR HORIZON</div>
              <div className="mail-snippet">Steve — we're running thin on Premium licenses. Cyber group is sharing 4 across 11 examiners…</div>
              <div className="mail-tags">
                <span className="mail-tg act">Action</span>
                <button onClick={triageBtn('cellebrite')} className="mail-triage-btn" data-triage="cellebrite"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('A. Chen') }}>{_avatarInitials('A. Chen')}</span>
                <div className="mail-from">OMB Budget Liaison — A. Chen</div>
                <div className="mail-time">Mon</div>
              </div>
              <div className="mail-subj">Exhibit 300 — Semi-annual due 06 JUN 26 (HSI investments)</div>
              <div className="mail-snippet">Reminder: LIMS modernization and IDEAL 2.0 Exhibit 300 packages due to OMB by 06 Jun. SOF and IT Dashboard…</div>
              <div className="mail-tags">
                <span className="mail-tg fyi">FYI</span>
                <button onClick={triageBtn('exhibit300')} className="mail-triage-btn" data-triage="exhibit300"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('J. Hartwell') }}>{_avatarInitials('J. Hartwell')}</span>
                <div className="mail-from">J. Hartwell (COR)</div>
                <div className="mail-time">Mon</div>
              </div>
              <div className="mail-subj">Apr '26 ITPMS invoice ready for COR review — $487,210.44</div>
              <div className="mail-snippet">Steve, attaching the April invoice and burn report. Numbers reconcile to spend plan w/ minor variance…</div>
              <div className="mail-tags">
                <span className="mail-tg act">Action</span>
                <button onClick={triageBtn('invoice')} className="mail-triage-btn" data-triage="invoice"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('M. Okonkwo') }}>{_avatarInitials('M. Okonkwo')}</span>
                <div className="mail-from">HSIA Director — M. Okonkwo</div>
                <div className="mail-time">Mon</div>
              </div>
              <div className="mail-subj">QBR read-ahead due Wed 1800 — please send your ITPMS enterprise roll-up</div>
              <div className="mail-snippet">Steve, need your one-pager (ITPMS enterprise roll-up · all 6 customer areas) by COB Wednesday. I'll consolidate w/ NER and SWR for…</div>
              <div className="mail-tags">
                <span className="mail-tg imp">Important</span>
                <button onClick={triageBtn('qbr')} className="mail-triage-btn" data-triage="qbr"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('ICE Cybersecurity') }}>{_avatarInitials('ICE Cybersecurity')}</span>
                <div className="mail-from">ICE Cybersecurity — IR Team</div>
                <div className="mail-time">Mon</div>
              </div>
              <div className="mail-subj">[ADVISORY] Critical patch — CVE-2026-1847 (auth bypass) · all internal apps</div>
              <div className="mail-snippet">Patch window starts 10 May 0200 EDT. DHS impacted systems: Lab gateway, RAVEn web tier…</div>
              <div className="mail-tags">
                <span className="mail-tg imp">Important</span>
                <button onClick={triageBtn('cve')} className="mail-triage-btn" data-triage="cve"><Bolt /> Triage</button>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('SCFL Houston') }}>{_avatarInitials('SCFL Houston')}</span>
                <div className="mail-from">SCFL (Houston) — IT POC</div>
                <div className="mail-time">Sun</div>
              </div>
              <div className="mail-subj">Confirming maint window 05 MAY 2200–0400 EDT — network refresh</div>
              <div className="mail-snippet">All hands aware. C3 Lab (Fairfax) covering after-hours intake. No mission impact projected…</div>
              <div className="mail-tags">
                <span className="mail-tg fyi">FYI</span>
              </div>
            </div>

            <div className="mail-item unread">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('S. Palmer') }}>{_avatarInitials('S. Palmer')}</span>
                <div className="mail-from">S. Palmer (ITPMS)</div>
                <div className="mail-time">Sun</div>
              </div>
              <div className="mail-subj">RAVEn-FL · ITAR ready for federal sig — Cellebrite expansion package</div>
              <div className="mail-snippet">Steve, market research, BWS, and Privacy checklist in the FedSpace approval queue…</div>
              <div className="mail-tags">
                <span className="mail-tg tsk">Sign-off</span>
              </div>
            </div>

            <div className="mail-item">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('C. Mendoza') }}>{_avatarInitials('C. Mendoza')}</span>
                <div className="mail-from">C. Mendoza — Lab BSA Lead</div>
                <div className="mail-time">Fri</div>
              </div>
              <div className="mail-subj">IDEAL 2.0 examiner usability pilot — 2 weeks of feedback compiled</div>
              <div className="mail-snippet">Hi Steve — encouraging signal from UF, FSU, UCF. Top friction is multi-step F-1 advisor flow…</div>
            </div>

            <div className="mail-item">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('Hurricane Prep IPT') }}>{_avatarInitials('Hurricane Prep IPT')}</span>
                <div className="mail-from">Hurricane Prep IPT</div>
                <div className="mail-time">Fri</div>
              </div>
              <div className="mail-subj">COOP TTX agenda · Friday 09 MAY · C3 Operations Center</div>
              <div className="mail-snippet">Tabletop scenario this year is a Cat-3 landfall near Atlantic seaboard with 72hr lead. Goal is to test…</div>
            </div>

            <div className="mail-item">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('HR') }}>{_avatarInitials('HR')}</span>
                <div className="mail-from">HR — D. Reyes onboarding</div>
                <div className="mail-time">Thu</div>
              </div>
              <div className="mail-subj">Wk 2 check-in materials for new GS-13 Program Analyst</div>
              <div className="mail-snippet">Daniel's PIV is provisioned. Access to DHS Confluence pending your sponsor sig…</div>
            </div>

            <div className="mail-item">
              <div className="mail-row1">
                <span className="mail-av" style={{ background: _avatarColor('K. Park') }}>{_avatarInitials('K. Park')}</span>
                <div className="mail-from">GAO Audit Liaison — K. Park</div>
                <div className="mail-time">Thu</div>
              </div>
              <div className="mail-subj">GAO-26-308 — HSI IT investments oversight · response due 16 May</div>
              <div className="mail-snippet">Ms. Null, attaching draft audit findings for fact check. DHS portfolio mentioned in 2 findings…</div>
              <div className="mail-tags">
                <span className="mail-tg imp">Important</span>
                <button onClick={triageBtn('gao')} className="mail-triage-btn" data-triage="gao"><Bolt /> Triage</button>
              </div>
            </div>
            </>
            )}
          </div>
        </div>

        {/* DETAIL */}
        <div className="mail-detail-box">
          <div className="mail-detail">
            <div className="mail-detail-h">
              <div className="mail-d-titlerow">
                <div className="mail-d-subj">[DELIVERABLE] Weekly Contractor Status — Wk Ending 02 May 2026</div>
                <span className="mail-d-cls">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M8 1.5l5.5 2v4c0 3.5-2.3 6.2-5.5 7-3.2-.8-5.5-3.5-5.5-7v-4z" />
                  </svg>
                  UNCLASSIFIED // FOR OFFICIAL USE ONLY
                </span>
              </div>
              <div className="mail-d-meta">
                <div className="mail-d-av" style={{ background: _avatarColor('Steve Palmer') }}>SP</div>
                <div className="mail-d-meta-l">
                  <div className="mail-d-from">Steve Palmer (ITPMS Program Manager)</div>
                  <div className="mail-d-to">
                    <strong>To:</strong>&nbsp;susan.williamson@ice.dhs.gov, j.hartwell@ice.dhs.gov (COR) · <strong>Cc:</strong>&nbsp;m.okonkwo@ice.dhs.gov
                  </div>
                </div>
                <div className="mail-d-meta-r">
                  <div className="mail-d-iconrow">
                    <button className="mail-d-icon" title="Reply" onClick={() => pbReplySelected()}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M7 3L2 8l5 5M2 8h7a5 5 0 0 1 5 5v1" />
                      </svg>
                    </button>
                    <button className="mail-d-icon" title="Forward" onClick={() => pbForwardSelected()}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M9 3l5 5-5 5M14 8H4a3 3 0 0 0-3 3v1" />
                      </svg>
                    </button>
                    <button className="mail-d-icon" title="Flag" onClick={() => pbFlagSelected()}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M3 14V2" />
                        <path d="M3 2h9l-2 3 2 3H3z" fill="currentColor" />
                      </svg>
                    </button>
                    <button className="mail-d-icon" title="Convert to Tasker" onClick={() => pbConvertSelected()}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <rect x="2" y="3" width="12" height="10" rx="1.3" />
                        <path d="M5 6.5h6M5 9h4" />
                      </svg>
                    </button>
                    <button className="mail-d-icon ai-summarize-btn" title="Summarize with AI" onClick={() => setAiEmailSummaryOpen(true)}>
                      <Spark />
                    </button>
                    <button className="mail-d-icon mail-d-icon--danger" title="Delete" onClick={() => pbDeleteMessage(inboxStateRef.current.selected?.id || '')}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4.5 4.5l.6 8.4a1 1 0 0 0 1 .93h3.8a1 1 0 0 0 1-.93l.6-8.4" />
                      </svg>
                    </button>
                  </div>
                  <div className="mail-d-time">Thu 07 May · <strong>07:42 ET</strong></div>
                </div>
              </div>
              <div className="mail-d-fromemail">steve.palmer@itpms.contractor.dhs.gov</div>
            </div>
            <div className="mail-d-body">
              <p>Steve,</p>
              <p>Per PWS §6.4, attaching the weekly status report covering the period ending Friday, 02 May 2026. Reviewed at our Monday IPT this morning. Highlights below; full detail in the deliverable on SharePoint.</p>
              <p><strong>Accomplishments — past week:</strong></p>
              <ul>
                <li>RAVEn-FL pilot reached first test gate (Cellebrite integration) — on schedule, no defects beyond minor</li>
                <li>DHS ADE-3 gap analysis draft delivered to OCIO IRB (per your request 22 Apr)</li>
                <li>IDEAL 2.0 examiner usability pilot complete — 14 days, 4 sections, structured feedback compiled</li>
                <li>Apr invoice + burn report submitted to J. Hartwell (COR)</li>
                <li>4 Branch taskers closed this week; 2 routed to C3 Lab (Fairfax) IT for action</li>
              </ul>
              <p><strong>Planned — coming week:</strong></p>
              <ul>
                <li>RAVEn-FL test gate 2 (Palantir handoff)</li>
                <li>DHS ADE-3 walkthrough w/ IRB (Tue 05 May, 1000)</li>
                <li>QBR slide pack (your one-pager owed Wed 1800)</li>
                <li>OMB Exhibit 300 prep — LIMS modernization, IDEAL 2.0</li>
                <li>Hurricane COOP TTX support</li>
              </ul>
              <p><strong>Risks:</strong></p>
              <ul>
                <li><strong>(R-RAV-019) RAVEn-FL Palantir handoff</strong> — license seat shortfall flagged by C3 Lab (Fairfax); PR pending your sig will resolve. Mitigation in flight.</li>
                <li><strong>(R-HSI-007) DHS ADE-3</strong> — 2 stakeholder dependencies still open w/ HSI HQ. Will discuss at IRB walkthrough.</li>
              </ul>
              <p><strong>Asks:</strong></p>
              <ul>
                <li>Sig on Cellebrite ITAR (in your FedSpace queue, 2d)</li>
                <li>Approval of D. Reyes Confluence sponsor (HR submitted Thu)</li>
              </ul>
              <p>Happy to walk through any item. Available all week except Friday morning (ITPMS internal review).</p>
              <div className="mail-d-sig">
                <strong>Steve Palmer, PMP, FAC-P/PM</strong><br />
                Program Manager · ITPMS Task Order #70CTD020FR0000280<br />
                Contractor support to ICE OCIO/SDD/HSI IT Portfolio Delivery Branch<br />
                <span className="ib-sig-contact">
                  <span className="ico">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                      <path d="M2 4h12v8H2z" />
                      <path d="M2 4l6 4 6-4" />
                    </svg>
                  </span> steve.palmer@itpms.contractor.dhs.gov · <span className="ico">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                      <rect x="4" y="1.5" width="8" height="13" rx="1.5" />
                      <path d="M7 13h2" />
                    </svg>
                  </span> (202) 555‑0148
                </span>
              </div>
              <div className="mail-d-replyrow">
                <button className="mail-d-replybtn" onClick={() => pbReplySelected()}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <path d="M7 3L2 8l5 5M2 8h7a5 5 0 0 1 5 5v1" />
                  </svg> Reply
                </button>
                <button className="mail-d-replybtn" onClick={() => pbForwardSelected()}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <path d="M9 3l5 5-5 5M14 8H4a3 3 0 0 0-3 3v1" />
                  </svg> Forward
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {workRequestOpen && <WorkRequestModal onClose={() => setWorkRequestOpen(false)} />}

      {snTicket && <ServiceNowTicketModal incident={snTicket} onClose={() => setSnTicket(null)} />}

      {aiEmailSummaryOpen && (() => {
        const email = pbCurrentEmail();
        const prompt = [
          `Summarize this email for someone catching up. Cover: what it's asking for, any deadlines, and anything that looks urgent or needs follow-up.`,
          'Keep it to a short paragraph plus a bullet list of action items if there are any.',
          '',
          `From: ${email.from || '(unknown)'}`,
          `Subject: ${email.subject || '(no subject)'}`,
          `Body: ${email.body || '(no content)'}`,
        ].join('\n');
        return (
          <AiSummaryModal
            title={email.subject || 'Email'}
            subtitle={email.from}
            prompt={prompt}
            onClose={() => setAiEmailSummaryOpen(false)}
            onTriage={() => {
              setAiEmailSummaryOpen(false);
              triageEmailLive(email as OutlookMessage);
            }}
          />
        );
      })()}

      {/* Modal */}
      {modal && (
        <div className="mo-overlay" id="mo" onClick={() => setModal(null)}>
          <div className="mo-card xl" onClick={(e) => e.stopPropagation()}>
            <div className="mo-head">
              <div className="mo-title" dangerouslySetInnerHTML={{ __html: modal.title }} />
              <button className="mo-close" onClick={() => setModal(null)} aria-label="Close">×</button>
            </div>
            <div className="mo-body" id="mb" dangerouslySetInnerHTML={{ __html: modal.body }} />
            <div className="mo-foot" id="mf" dangerouslySetInnerHTML={{ __html: modal.footer }} />
          </div>
        </div>
      )}
    </div>
  );
}
